import {
  type BitcoinConfig,
  type DynamicWalletClient,
  ThresholdSignatureScheme,
  WalletOperation,
} from '@dynamic-labs-wallet/browser-wallet-client';
import { MFAAction } from '@dynamic-labs/sdk-api-core';

import { getCore } from '../../../client/core/getCore';
import type { DynamicClient } from '../../../client/types/DynamicClient';
import { InvalidParamError } from '../../../errors/InvalidParamError';
import type { Chain } from '../../chain';
import { consumeMfaTokenIfRequiredForAction } from '../../mfa/consumeMfaTokenIfRequiredForAction';
import { getSignedSessionId } from '../../sessionKeys/getSignedSessionId';
import { createWaasClient } from '../createWaasClient';
import { getWaasChainNameFromChain } from '../getWaasChainNameFromChain';
import type { WaasProvider } from '../waas.types';

const RAW_MESSAGE_MESSAGE_REQUIRED_LENGTH = 64;

type CreateWaasProviderParams = {
  chain: Chain;
  sdkClient: DynamicClient;
};

export const createWaasProvider = ({
  sdkClient,
  chain,
}: CreateWaasProviderParams): WaasProvider => {
  const logger = getCore(sdkClient).logger;

  let _waasClient: DynamicWalletClient | undefined;

  // because the waas client needs the authToken to be set, we can only create it
  // the first time it's needed, which is after the user has logged in
  const getWaasClient: WaasProvider['getWaasClient'] = async () => {
    if (!_waasClient) {
      _waasClient = createWaasClient(
        { chainName: getWaasChainNameFromChain(chain) },
        sdkClient
      );
      await _waasClient.initialize();
    }
    return _waasClient;
  };

  const backupKeySharesToGoogleDrive: WaasProvider['backupKeySharesToGoogleDrive'] =
    async ({ password, walletAccount }) => {
      const { signature: signedSessionId } = await getSignedSessionId(
        sdkClient
      );

      const waasClient = await getWaasClient();

      return waasClient.backupKeySharesToGoogleDrive({
        accountAddress: walletAccount.address,
        authToken: sdkClient.token ?? undefined,
        password,
        signedSessionId,
      });
    };

  const createWalletAccount: WaasProvider['createWalletAccount'] = async (
    args = {}
  ) => {
    logger.debug('[createWaasProvider] createWalletAccount', {
      bitcoinConfig: args.bitcoinConfig,
      thresholdSignatureScheme: args.thresholdSignatureScheme,
    });

    const { signature: signedSessionId } = await getSignedSessionId(sdkClient);

    const waasClient = await getWaasClient();

    const waasWallet = await waasClient.createWalletAccount({
      authToken: sdkClient.token ?? undefined,
      bitcoinConfig: args.bitcoinConfig as BitcoinConfig | undefined,
      password: args.password,
      signedSessionId,
      thresholdSignatureScheme:
        args.thresholdSignatureScheme ?? ThresholdSignatureScheme.TWO_OF_TWO,
    });

    return waasWallet;
  };

  const delegateKeyShares: WaasProvider['delegateKeyShares'] = async ({
    password,
    walletAccount,
  }) => {
    const { signature: signedSessionId } = await getSignedSessionId(sdkClient);

    const waasClient = await getWaasClient();

    return waasClient.delegateKeyShares({
      accountAddress: walletAccount.address,
      authToken: sdkClient.token ?? undefined,
      password,
      signedSessionId,
    });
  };

  const exportClientKeyshares: WaasProvider['exportClientKeyshares'] = async ({
    password,
    walletAccount,
  }) => {
    const { signature: signedSessionId } = await getSignedSessionId(sdkClient);

    const waasClient = await getWaasClient();

    await waasClient.exportClientKeyshares({
      accountAddress: walletAccount.address,
      authToken: sdkClient.token ?? undefined,
      password,
      signedSessionId,
    });
  };

  const exportPrivateKey: WaasProvider['exportPrivateKey'] = async ({
    displayContainer,
    walletAccount,
    password,
  }) => {
    const { signature: signedSessionId } = await getSignedSessionId(sdkClient);

    const waasClient = await getWaasClient();

    const mfaToken = await consumeMfaTokenIfRequiredForAction(
      { mfaAction: MFAAction.WalletWaasExport },
      sdkClient
    );

    await waasClient.exportPrivateKey({
      accountAddress: walletAccount.address,
      authToken: sdkClient.token ?? undefined,
      displayContainer,
      mfaToken,
      password,
      signedSessionId,
    });
  };

  const restoreUserShareForWalletAccount: WaasProvider['restoreUserShareForWalletAccount'] =
    async ({ walletAccount }) => {
      const { signature: signedSessionId } = await getSignedSessionId(
        sdkClient
      );

      const waasClient = await getWaasClient();

      await waasClient.getWallet({
        accountAddress: walletAccount.address,
        authToken: sdkClient.token ?? undefined,
        signedSessionId,
        walletOperation: WalletOperation.SIGN_MESSAGE,
      });
    };

  const importPrivateKey: WaasProvider['importPrivateKey'] = async ({
    privateKey,
    thresholdSignatureScheme = ThresholdSignatureScheme.TWO_OF_TWO,
  }) => {
    const { signature: signedSessionId } = await getSignedSessionId(sdkClient);

    const walletClient = await getWaasClient();

    await walletClient.importPrivateKey({
      authToken: sdkClient.token ?? undefined,
      privateKey,
      signedSessionId,
      thresholdSignatureScheme,
    });
  };

  const refreshWalletAccountShares: WaasProvider['refreshWalletAccountShares'] =
    async ({ password, walletAccount }) => {
      const { signature: signedSessionId } = await getSignedSessionId(
        sdkClient
      );

      const walletClient = await getWaasClient();

      const mfaToken = await consumeMfaTokenIfRequiredForAction(
        { mfaAction: MFAAction.WalletWaasRefresh },
        sdkClient
      );

      return walletClient.refreshWalletAccountShares({
        accountAddress: walletAccount.address,
        authToken: sdkClient.token ?? undefined,
        mfaToken,
        password,
        signedSessionId,
      });
    };

  const revokeDelegation: WaasProvider['revokeDelegation'] = async ({
    password,
    walletAccount,
  }) => {
    const { signature: signedSessionId } = await getSignedSessionId(sdkClient);

    const waasClient = await getWaasClient();

    return waasClient.revokeDelegation({
      accountAddress: walletAccount.address,
      authToken: sdkClient.token ?? undefined,
      password,
      signedSessionId,
    });
  };

  const setWaasWalletAccountPassword: WaasProvider['setWaasWalletAccountPassword'] =
    async ({ password, walletAccount }) => {
      const { signature: signedSessionId } = await getSignedSessionId(
        sdkClient
      );

      const waasClient = await getWaasClient();

      return waasClient.setPassword({
        accountAddress: walletAccount.address,
        authToken: sdkClient.token ?? undefined,
        newPassword: password,
        signedSessionId,
      });
    };

  const signRawMessage: WaasProvider['signRawMessage'] = async ({
    message,
    password,
    walletAccount,
  }) => {
    if (message.length !== RAW_MESSAGE_MESSAGE_REQUIRED_LENGTH) {
      throw new InvalidParamError(
        `Message must be ${RAW_MESSAGE_MESSAGE_REQUIRED_LENGTH} characters long`
      );
    }

    const { signature: signedSessionId } = await getSignedSessionId(sdkClient);

    const mfaToken = await consumeMfaTokenIfRequiredForAction(
      { mfaAction: MFAAction.WalletWaasSign },
      sdkClient
    );

    const walletClient = await getWaasClient();

    const signature = await walletClient.signRawMessage({
      accountAddress: walletAccount.address,
      authToken: sdkClient.token ?? undefined,
      message,
      mfaToken,
      password,
      signedSessionId,
    });

    return { signature };
  };

  const terminate: WaasProvider['terminate'] = async () => {
    if (!_waasClient) {
      return;
    }

    const waasClient = await getWaasClient();
    await waasClient.cleanup();
    _waasClient = undefined;
  };

  const updatePassword: WaasProvider['updatePassword'] = async ({
    walletAccount,
    currentPassword,
    newPassword,
  }) => {
    const { signature: signedSessionId } = await getSignedSessionId(sdkClient);

    const waasClient = await getWaasClient();

    return waasClient.updatePassword({
      accountAddress: walletAccount.address,
      authToken: sdkClient.token ?? undefined,
      existingPassword: currentPassword,
      newPassword,
      signedSessionId,
    });
  };

  const signMessage: WaasProvider['signMessage'] = async ({
    message,
    walletAccount,
  }) => {
    const { signature: signedSessionId } = await getSignedSessionId(sdkClient);

    const mfaToken = await consumeMfaTokenIfRequiredForAction(
      { mfaAction: MFAAction.WalletWaasSign },
      sdkClient
    );

    const waasClient = await getWaasClient();

    const signature = await waasClient.signMessage({
      accountAddress: walletAccount.address,
      authToken: sdkClient.token ?? undefined,
      message,
      mfaToken,
      signedSessionId,
    });

    return { signature };
  };

  const signSerializedTransaction: WaasProvider['signSerializedTransaction'] =
    async ({ chainId, serializedTransaction, walletAccount }) => {
      const { signature: signedSessionId } = await getSignedSessionId(
        sdkClient
      );

      const mfaToken = await consumeMfaTokenIfRequiredForAction(
        { mfaAction: MFAAction.WalletWaasSign },
        sdkClient
      );

      const waasClient = await getWaasClient();

      const signature = await waasClient.signTransaction({
        authToken: sdkClient.token ?? undefined,
        chainId,
        mfaToken,
        senderAddress: walletAccount.address,
        signedSessionId,
        transaction: serializedTransaction,
      });

      return { signature };
    };

  const getWalletRecoveryState: WaasProvider['getWalletRecoveryState'] =
    async ({ walletAccount }) => {
      const { signature: signedSessionId } = await getSignedSessionId(
        sdkClient
      );

      const waasClient = await getWaasClient();

      return waasClient.getWalletRecoveryState({
        accountAddress: walletAccount.address,
        authToken: sdkClient.token ?? undefined,
        signedSessionId,
      });
    };

  const unlockWallet: WaasProvider['unlockWallet'] = async ({
    password,
    walletAccount,
  }) => {
    const { signature: signedSessionId } = await getSignedSessionId(sdkClient);

    const waasClient = await getWaasClient();

    return waasClient.unlockWallet({
      accountAddress: walletAccount.address,
      authToken: sdkClient.token ?? undefined,
      password,
      signedSessionId,
    });
  };

  return {
    backupKeySharesToGoogleDrive,
    createWalletAccount,
    delegateKeyShares,
    exportClientKeyshares,
    exportPrivateKey,
    getWaasClient,
    getWalletRecoveryState,
    importPrivateKey,
    refreshWalletAccountShares,
    restoreUserShareForWalletAccount,
    revokeDelegation,
    setWaasWalletAccountPassword,
    signMessage,
    signRawMessage,
    signSerializedTransaction,
    terminate,
    unlockWallet,
    updatePassword,
  };
};
